import { effect, init, surface } from 'vgpu';

const NS = 'http://www.w3.org/2000/svg';

const nodes = [
  {
    id: 'intake', x: 155, y: 155, index: 'A1', label: 'Fragmented intake', meta: 'Observed signal',
    summary: 'The same client details are copied across several systems before work can begin.',
    evidence: ['Firsthand account', 'High · 0.86', 'One workflow'],
    causality: ['Upstream condition', 'Strong influence', 'Repeated weekly'],
    decision: ['Outside aperture', 'Monitor only', 'No owner named'],
  },
  {
    id: 'rework', x: 390, y: 290, index: 'B2', label: 'Rework loop', meta: 'Process dynamic',
    summary: 'Repeated entry creates corrections, context switching, and small recoveries that compound over time.',
    evidence: ['Two corrections cited', 'Medium · 0.68', 'Frequency unknown'],
    causality: ['Reinforcing loop', 'High influence', 'Amplifies delay'],
    decision: ['Candidate boundary', 'Instrument first', 'Human review'],
  },
  {
    id: 'latency', x: 635, y: 178, index: 'C3', label: 'Decision latency', meta: 'Consequence',
    summary: 'People wait for a complete picture, while the work needed to assemble that picture adds more delay.',
    evidence: ['Derived hypothesis', 'Low · 0.42', 'Needs timing data'],
    causality: ['Downstream effect', 'Medium influence', 'Two inbound paths'],
    decision: ['Measure baseline', 'Owner required', 'Reversible'],
  },
  {
    id: 'handoff', x: 610, y: 435, index: 'C4', label: 'Unclear handoff', meta: 'Constraint',
    summary: 'No confirmed owner exists for the transition between intake, correction, and action.',
    evidence: ['Role unknown', 'Medium · 0.61', 'Attribution open'],
    causality: ['Structural friction', 'Medium influence', 'Feeds latency'],
    decision: ['Resolve owner', 'Conversation', 'Before automation'],
  },
  {
    id: 'intervention', x: 830, y: 320, index: 'D5', label: 'Bounded intervention', meta: 'Decision point', decisionNode: true,
    summary: 'Test one structured intake and a visible review step without replacing the systems around it.',
    evidence: ['Proposed response', 'Not yet tested', 'One intake path'],
    causality: ['Interrupting move', 'Hypothesized', 'Breaks rework'],
    decision: ['Inside aperture', 'Run a 2-week test', 'Human-owned'],
  },
  {
    id: 'evidence', x: 870, y: 505, index: 'E6', label: 'Time to evidence', meta: 'Outcome measure',
    summary: 'Measure whether the team reaches a reviewable, decision-ready record sooner and with fewer corrections.',
    evidence: ['Metric defined', 'Observable', 'Baseline missing'],
    causality: ['Validation signal', 'Tests the model', 'May falsify path'],
    decision: ['Success criterion', 'Compare to baseline', 'Stop if worse'],
  },
];

const edges = [
  ['intake', 'rework', 'primary'],
  ['rework', 'latency', 'primary'],
  ['rework', 'handoff', 'secondary'],
  ['handoff', 'latency', 'primary'],
  ['latency', 'intervention', 'primary'],
  ['handoff', 'intervention', 'secondary'],
  ['intervention', 'evidence', 'primary'],
];

const lensCopy = {
  evidence: {
    number: '01', name: 'Evidence lens', reading: 'Evidence reading',
    labels: ['Source', 'Confidence', 'Boundary'],
    question: 'Which parts were directly observed, and which were inferred afterward?',
  },
  causality: {
    number: '02', name: 'Causality lens', reading: 'Causal reading',
    labels: ['Role in path', 'Influence', 'Pattern'],
    question: 'If this node changed while the others held still, what would we expect to move?',
  },
  decision: {
    number: '03', name: 'Decision lens', reading: 'Decision reading',
    labels: ['Aperture', 'Next move', 'Guardrail'],
    question: 'What is the smallest reversible move a named human can actually authorize?',
  },
};

const nodeLayer = document.querySelector('#node-layer');
const edgeLayer = document.querySelector('#edge-layer');
const graphStage = document.querySelector('#graph-stage');
const graphViewport = document.querySelector('#graph-viewport');
const graphWorld = document.querySelector('#graph-world');
const focusWaves = document.querySelectorAll('.focus-wave');
const zoomLevel = document.querySelector('#zoom-level');
let activeLens = 'evidence';
let focusedNode = nodes[0];
let userZoom = 1;
let pinchState = null;

const byId = Object.fromEntries(nodes.map((node) => [node.id, node]));
const svgEl = (name, attrs = {}) => {
  const el = document.createElementNS(NS, name);
  Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
  return el;
};

function edgePath(from, to) {
  const dx = to.x - from.x;
  const bend = Math.max(45, Math.abs(dx) * 0.34);
  return `M ${from.x + 31} ${from.y} C ${from.x + bend} ${from.y}, ${to.x - bend} ${to.y}, ${to.x - 35} ${to.y}`;
}

edges.forEach(([fromId, toId, kind]) => {
  const path = svgEl('path', {
    d: edgePath(byId[fromId], byId[toId]),
    class: `edge ${kind}`,
    'data-from': fromId,
    'data-to': toId,
  });
  edgeLayer.append(path);
});

nodes.forEach((node) => {
  const group = svgEl('g', {
    class: `node${node.decisionNode ? ' is-decision' : ''}`,
    transform: `translate(${node.x} ${node.y})`,
    tabindex: '0',
    role: 'button',
    'aria-label': `${node.label}, ${node.meta}`,
    'data-node': node.id,
  });
  group.append(svgEl('circle', { class: 'halo', r: '58' }));
  group.append(svgEl('circle', { class: 'core', r: '31' }));
  const index = svgEl('text', { class: 'index', x: '-8', y: '4' });
  index.textContent = node.index;
  group.append(index);
  const label = svgEl('text', { class: 'label', x: '49', y: '-4' });
  label.textContent = node.label;
  group.append(label);
  const meta = svgEl('text', { class: 'meta', x: '49', y: '16' });
  meta.textContent = node.meta;
  group.append(meta);
  group.addEventListener('click', () => focus(node));
  group.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      focus(node);
    }
  });
  nodeLayer.append(group);
});

function focus(node) {
  focusedNode = node;
  const connected = new Set();
  edges.forEach(([from, to]) => {
    if (from === node.id) connected.add(to);
    if (to === node.id) connected.add(from);
  });

  document.querySelectorAll('.node').forEach((el) => {
    const isFocus = el.dataset.node === node.id;
    const isContext = connected.has(el.dataset.node);
    el.classList.toggle('is-focused', isFocus);
    el.classList.toggle('is-context', !isFocus && isContext);
    el.classList.toggle('is-muted', !isFocus && !isContext);
  });
  document.querySelectorAll('.edge').forEach((el) => {
    el.classList.toggle('is-lit', el.dataset.from === node.id || el.dataset.to === node.id);
  });

  graphWorld.style.transformOrigin = `${node.x}px ${node.y}px`;
  graphWorld.classList.add('is-focused');
  focusWaves.forEach((wave) => {
    wave.setAttribute('cx', node.x);
    wave.setAttribute('cy', node.y);
  });

  const mode = lensCopy[activeLens];
  const values = node[activeLens];
  document.querySelector('#reading-mode').textContent = mode.reading;
  document.querySelector('#reading-type').textContent = node.meta;
  document.querySelector('#reading-title').textContent = node.label;
  document.querySelector('#reading-summary').textContent = node.summary;
  document.querySelector('#focus-mode').textContent = `${activeLens} focus`;
  document.querySelector('#focus-node').textContent = node.label;
  ['a', 'b', 'c'].forEach((key, index) => {
    document.querySelector(`#measure-${key}-label`).textContent = mode.labels[index];
    document.querySelector(`#measure-${key}-value`).textContent = values[index];
  });
  document.querySelector('#focus-question').textContent = mode.question;
}

function selectLens(name) {
  activeLens = name;
  document.querySelectorAll('.lens-tab').forEach((tab) => {
    const active = tab.dataset.lens === name;
    tab.classList.toggle('is-active', active);
    tab.setAttribute('aria-selected', String(active));
  });
  graphWorld.classList.remove('mode-evidence', 'mode-causality', 'mode-decision');
  graphWorld.classList.add(`mode-${name}`);
  focus(focusedNode);
}

document.querySelectorAll('.lens-tab').forEach((tab) => {
  tab.addEventListener('click', () => selectLens(tab.dataset.lens));
});

const touchDistance = (touches) => Math.hypot(
  touches[0].clientX - touches[1].clientX,
  touches[0].clientY - touches[1].clientY,
);

function zoomOrigin(touches) {
  const rect = graphStage.querySelector('.graph').getBoundingClientRect();
  const centerX = (touches[0].clientX + touches[1].clientX) / 2;
  const centerY = (touches[0].clientY + touches[1].clientY) / 2;
  return {
    x: Math.max(0, Math.min(1000, ((centerX - rect.left) / rect.width) * 1000)),
    y: Math.max(0, Math.min(620, ((centerY - rect.top) / rect.height) * 620)),
  };
}

function applyUserZoom(nextZoom, origin) {
  userZoom = Math.max(0.85, Math.min(2.2, nextZoom));
  if (origin) graphViewport.style.transformOrigin = `${origin.x}px ${origin.y}px`;
  graphViewport.style.transform = `scale(${userZoom})`;
  zoomLevel.textContent = `${Math.round(userZoom * 100)}%`;
}

graphStage.addEventListener('touchstart', (event) => {
  if (event.touches.length !== 2) return;
  event.preventDefault();
  pinchState = {
    distance: touchDistance(event.touches),
    zoom: userZoom,
    origin: zoomOrigin(event.touches),
  };
  graphViewport.classList.add('is-pinching');
}, { passive: false });

graphStage.addEventListener('touchmove', (event) => {
  if (event.touches.length !== 2 || !pinchState) return;
  event.preventDefault();
  const ratio = touchDistance(event.touches) / Math.max(1, pinchState.distance);
  applyUserZoom(pinchState.zoom * ratio, pinchState.origin);
}, { passive: false });

graphStage.addEventListener('touchend', (event) => {
  if (event.touches.length < 2) {
    pinchState = null;
    graphViewport.classList.remove('is-pinching');
  }
});

graphStage.addEventListener('wheel', (event) => {
  if (!event.ctrlKey && !event.metaKey) return;
  event.preventDefault();
  const rect = graphStage.querySelector('.graph').getBoundingClientRect();
  const origin = {
    x: ((event.clientX - rect.left) / rect.width) * 1000,
    y: ((event.clientY - rect.top) / rect.height) * 620,
  };
  applyUserZoom(userZoom * Math.exp(-event.deltaY * 0.002), origin);
}, { passive: false });

graphStage.addEventListener('dblclick', () => applyUserZoom(1, { x: 500, y: 310 }));

window.__lensLab = {
  getZoom: () => userZoom,
  setZoom: (zoom, x = 500, y = 310) => applyUserZoom(zoom, { x, y }),
};

document.querySelectorAll('.scenario-prism button').forEach((button) => {
  button.addEventListener('click', () => {
    selectLens(button.dataset.mode);
    focus(byId[button.dataset.focus]);
  });
});

selectLens('evidence');

async function startOpticalField() {
  const status = document.querySelector('#gpu-status');
  if (!navigator.gpu) {
    status.querySelector('span').textContent = 'WebGPU fallback active';
    return;
  }

  const shader = `
    fn hash(p: vec2f) -> f32 {
      return fract(sin(dot(p, vec2f(127.1, 311.7))) * 43758.5453);
    }

    @fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
      let p = uv - vec2f(0.58, 0.42);
      let radius = length(p * vec2f(1.0, 1.65));
      let ring = exp(-52.0 * abs(radius - 0.23));
      let soft = exp(-4.8 * radius) * 0.18;
      let sweep = pow(max(0.0, 1.0 - abs(p.x * 1.8 + p.y)), 8.0) * 0.055;
      let noise = (hash(floor(uv * 180.0)) - 0.5) * 0.012;
      let base = vec3f(0.018, 0.028, 0.021);
      let optic = vec3f(0.28, 0.42, 0.19) * (ring * 0.12 + soft);
      let spectral = vec3f(0.13, 0.26, 0.28) * sweep;
      return vec4f(base + optic + spectral + noise, 1.0);
    }
  `;

  try {
    const gpu = await init();
    const canvas = document.querySelector('#gpu-field');
    const target = surface(gpu, canvas);
    effect(gpu, shader).draw(target);
    status.classList.add('is-live');
    status.querySelector('span').textContent = 'vgpu optical field live';
  } catch (error) {
    console.warn('vgpu field unavailable; retaining CSS fallback.', error);
    status.querySelector('span').textContent = 'WebGPU fallback active';
  }
}

startOpticalField();
